import { useEffect, useState } from "react";
import { themeVar as colors } from "../design-tokens";
import { EXTRACT_STAGES, STAGE_LABEL, stageLabel } from "./constants";
import type { AmbJobPublic } from "./types";

/**
 * UI-050 part 2 — the extraction progress panel.
 *
 * The defect this closes: extraction takes ~34 seconds and the screen showed nothing at all. Part 1
 * (already shipped) kept `busy` true across the whole extract so the button stopped re-enabling on
 * an inert screen. This is the other half — the user now sees *what* is happening, *which file*,
 * and *how long it has been going*.
 *
 * The stage list is apex's `AMB_XSTAGES`, ported rather than re-invented: apex has shown these four
 * stages to real users for far longer than `/next` has existed. Labels come from `constants.ts`'s
 * `STAGE_LABEL`, shared with the build screen, so the two panels cannot drift into two vocabularies
 * for one server token.
 *
 * The elapsed counter shows SECONDS under a minute. Apex's own counter reports `0 min` for the
 * first sixty seconds — exactly the window in which a user decides nothing is happening — so this
 * one deliberately does not copy that behaviour.
 */

export type ExtractPhase = "idle" | "uploading" | "extracting";

/** Where the server is, expressed as an index into `EXTRACT_STAGES`. `-1` means "not there yet"
 * (the upload is still in flight and no job exists to report a stage). */
function stageIndex(job: AmbJobPublic | null): number {
  if (!job) return -1;
  if (job.status === "review_ready") return EXTRACT_STAGES.length - 1;
  const i = EXTRACT_STAGES.indexOf(job.stage as (typeof EXTRACT_STAGES)[number]);
  if (i >= 0) return i;
  // A stage the extract list doesn't name (`extract`, `queued`, `running`) means the read has begun
  // but OCR/transcription hasn't been announced — sit on the first stage rather than guessing ahead.
  return 0;
}

export function ExtractProgress({
  job,
  phase,
  fileNames,
  onStartOver,
}: {
  job: AmbJobPublic | null;
  phase: Exclude<ExtractPhase, "idle">;
  fileNames: string[];
  /** UI-101/NAVIGATION rule 6: a wait is not a dead-end. Always offer a way out. */
  onStartOver?: () => void;
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const current = stageIndex(job);
  const ocrActive = job?.stage === "ocr" || (job?.ocr?.pages != null && job?.status !== "review_ready");
  const primaryFile = fileNames[0];
  const headline =
    phase === "uploading"
      ? primaryFile
        ? `Uploading ${primaryFile}…`
        : "Uploading your file…"
      : primaryFile
        ? `Reading ${primaryFile}…`
        : "Reading your file…";

  return (
    <section
      aria-labelledby="vb-extract-heading"
      style={{ marginTop: 20, background: colors.bgSurface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 20 }}
    >
      <h2 id="vb-extract-heading" style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
        {headline}
      </h2>
      <p style={{ marginTop: 6, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
        {/* THE QUOTED TIME HAS TO COVER THE SLOW CASE (2026-08-04). "30-60 seconds" was measured on
            a file with a working text layer. A scanned or unmapped-font PDF is read a page at a
            time at roughly 13 seconds a page, so a real 11-page export takes over two minutes and a
            20-page one takes four — and the owner watched this line promise a minute while his own
            file sat at 2m 10s with a red warning above it. A number that is wrong most of the time
            it matters is worse than a range. */}
        Most files take under a minute. A scanned PDF, or one exported without a readable text
        layer, has to be read a page at a time &mdash; that can take up to 10 minutes on a long
        file, and it is working normally. You can leave this page open; it keeps going.
      </p>

      <ol style={{ listStyle: "none", margin: "16px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        {EXTRACT_STAGES.map((key, i) => {
          const isOcr = key === "ocr";
          const done = i < current || (job?.status === "review_ready" && i <= current);
          const active = i === current && !done ? true : isOcr ? Boolean(ocrActive) : false;
          const state: "done" | "active" | "pending" = done ? "done" : active ? "active" : "pending";
          return (
            <li key={key} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <span
                aria-hidden="true"
                style={{
                  marginTop: 3,
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  flex: "0 0 auto",
                  background: state === "done" ? colors.verified : state === "active" ? colors.verifiedBg : colors.neutralChip,
                  border: `1px solid ${state === "pending" ? colors.border : colors.verified}`,
                }}
              />
              <div>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: state === "active" ? 600 : 400,
                    color: state === "pending" ? colors.textSecondary : state === "active" ? colors.verified : colors.textPrimary,
                  }}
                >
                  {STAGE_LABEL[key]}
                </span>
                {isOcr && !ocrActive && (
                  <span style={{ fontSize: 12, color: colors.textSecondary }}> &mdash; only if your file is scanned</span>
                )}
                {isOcr && job?.ocr?.page != null && job?.ocr?.pages != null && (
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: colors.textSecondary }}>
                    Page {job.ocr.page} of {job.ocr.pages}
                    {job.ocr.file ? ` — ${job.ocr.file}` : ""}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* UI-054: the wait is announced, not just drawn. Politely, so it never interrupts typing. */}
      <p role="status" aria-live="polite" style={{ marginTop: 16, fontSize: 12, color: colors.textSecondary }}>
        {job ? stageLabel(job.stage) : "Uploading"} · {formatElapsed(elapsed)}
      </p>

      {fileNames.length > 1 && (
        <p style={{ marginTop: 6, fontSize: 12, color: colors.textSecondary }}>
          {fileNames.length} files: {fileNames.join(", ")}
        </p>
      )}

      {onStartOver && (
        <button className="vb-btn"
          type="button"
          onClick={onStartOver}
          style={{
            marginTop: 16,
            padding: "8px 14px",
            borderRadius: 8,
            border: `1px solid ${colors.border}`,
            background: colors.bgSurface,
            color: colors.textPrimary,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Cancel and start over
        </button>
      )}
    </section>
  );
}

/** Seconds under a minute — see this file's docstring for why that is deliberate. */
export function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s elapsed`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s elapsed`;
}
